import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Search, Plus, Edit2, Trash2, X, Upload, Check, ChevronDown, ChevronUp } from 'lucide-react';

// Social media icons (same as Creators page)
const TikTokIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const InstagramIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const LinkedInIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const YouTubeIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const GlobeIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const TwitterIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TwitchIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
  </svg>
);

interface Creator {
  id: string;
  display_name: string;
  profile_image_url: string | null;
  area: string | null;
  about_me: string | null;
  language: string;
  social_links?: {
    website?: string;
    youtube?: string;
    tiktok?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
    twitch?: string;
  };
  real_talk_sentence: string | null;
  real_talk_tags: string[] | string | null;
  hashtags: string[] | string | null;
  is_active: boolean;
  is_verified: boolean;
}

interface FormData {
  display_name: string;
  area: string;
  about_me: string;
  language: string;
  hashtags: string;
  website: string;
  youtube: string;
  tiktok: string;
  instagram: string;
  linkedin: string;
  twitter: string;
  twitch: string;
  real_talk_sentence: string;
  real_talk_tags: string;
  is_active: boolean;
}

const emptyForm: FormData = {
  display_name: '',
  area: '',
  about_me: '',
  language: 'de',
  hashtags: '',
  website: '',
  youtube: '',
  tiktok: '',
  instagram: '',
  linkedin: '',
  twitter: '',
  twitch: '',
  real_talk_sentence: '',
  real_talk_tags: '',
  is_active: true,
};

const CreatorManagement: React.FC = () => {
  const { apiCall } = useAuth();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Edit state
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  
  // Image upload state
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  // HARDCODED - env var has http:// which causes Mixed Content error
  const apiBaseUrl = 'https://api.cevee.ai';

  const fetchCreators = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall(`${apiBaseUrl}/api/v1/admin/creators?active_only=false`);
      if (!response.ok) throw new Error('Failed to fetch creators');
      const data = await response.json();
      setCreators(data.creators || []);
    } catch (err) {
      console.error('Error fetching creators:', err);
      setError('Failed to load creators');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl]); // apiCall excluded to prevent infinite loop

  useEffect(() => {
    fetchCreators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const selectCreator = (creator: Creator) => {
    setSelectedCreator(creator);
    setIsCreating(false);
    
    // Handle social_links
    const socialLinks = creator.social_links || {};
    
    // Handle hashtags - could be array or comma-separated string
    let hashtagsStr = '';
    if (Array.isArray(creator.hashtags)) {
      hashtagsStr = creator.hashtags.join(', ');
    } else if (typeof creator.hashtags === 'string') {
      hashtagsStr = creator.hashtags;
    }
    
    // Handle real_talk_tags - could be array or comma-separated string
    let realTalkTagsStr = '';
    if (Array.isArray(creator.real_talk_tags)) {
      realTalkTagsStr = creator.real_talk_tags.join(', ');
    } else if (typeof creator.real_talk_tags === 'string') {
      realTalkTagsStr = creator.real_talk_tags;
    }
    
    setFormData({
      display_name: creator.display_name || '',
      area: creator.area || '',
      about_me: creator.about_me || '',
      language: creator.language || 'de',
      hashtags: hashtagsStr,
      website: socialLinks.website || '',
      youtube: socialLinks.youtube || '',
      tiktok: socialLinks.tiktok || '',
      instagram: socialLinks.instagram || '',
      linkedin: socialLinks.linkedin || '',
      twitter: socialLinks.twitter || '',
      twitch: socialLinks.twitch || '',
      real_talk_sentence: creator.real_talk_sentence || '',
      real_talk_tags: realTalkTagsStr,
      is_active: creator.is_active,
    });
    // Use getImageUrl to build proper URL
    setImagePreview(getImageUrl(creator.profile_image_url));
    setImageFile(null);
  };
  
  // Build image URL like the real Creators page does
  const getImageUrl = (imageUrl: string | null): string | null => {
    if (!imageUrl) return null;
    
    // If it's a data URL (for preview), use as-is
    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    
    // If it's an Azure Blob URL, extract just the filename
    // Azure URLs look like: https://xxx.blob.core.windows.net/educator-images/creator-xxx.jpg
    let filename = imageUrl;
    if (imageUrl.startsWith('http')) {
      filename = imageUrl.split('/').pop() || imageUrl;
    }
    
    // Use the educators images proxy endpoint
    return `${apiBaseUrl}/api/v1/educators/images/${filename}`;
  };

  const startCreating = () => {
    setSelectedCreator(null);
    setIsCreating(true);
    setFormData(emptyForm);
    setImagePreview(null);
    setImageFile(null);
  };

  const cancelEdit = () => {
    setSelectedCreator(null);
    setIsCreating(false);
    setFormData(emptyForm);
    setImagePreview(null);
    setImageFile(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!window.confirm(isCreating ? 'Create this new creator?' : 'Save changes to this creator?')) return;
    
    try {
      setSaving(true);
      setError(null);

      // Prepare data
      const saveData = {
        display_name: formData.display_name,
        area: formData.area || null,
        about_me: formData.about_me || null,
        language: formData.language,
        hashtags: formData.hashtags ? formData.hashtags.split(',').map(t => t.trim()).filter(Boolean) : null,
        social_links: {
          website: formData.website || null,
          youtube: formData.youtube || null,
          tiktok: formData.tiktok || null,
          instagram: formData.instagram || null,
          linkedin: formData.linkedin || null,
          twitter: formData.twitter || null,
          twitch: formData.twitch || null,
        },
        real_talk_sentence: formData.real_talk_sentence || null,
        real_talk_tags: formData.real_talk_tags ? formData.real_talk_tags.split(',').map(t => t.trim()).filter(Boolean) : null,
        is_active: formData.is_active,
      };

      let creatorId = selectedCreator?.id;

      if (isCreating) {
        // Create new
        const response = await apiCall(`${apiBaseUrl}/api/v1/admin/creators`, {
          method: 'POST',
          body: JSON.stringify(saveData),
        });
        if (!response.ok) throw new Error('Failed to create creator');
        const newCreator = await response.json();
        creatorId = newCreator.id;
      } else if (selectedCreator) {
        // Update existing
        const response = await apiCall(`${apiBaseUrl}/api/v1/admin/creators/${selectedCreator.id}`, {
          method: 'PUT',
          body: JSON.stringify(saveData),
        });
        if (!response.ok) throw new Error('Failed to update creator');
      }

      // Upload image if changed
      if (imageFile && creatorId) {
        const formDataImg = new FormData();
        formDataImg.append('file', imageFile);
        
        const imgResponse = await apiCall(`${apiBaseUrl}/api/v1/admin/creators/${creatorId}/image`, {
          method: 'POST',
          body: formDataImg,
          headers: {}, // Let browser set content-type for FormData
        });
        if (!imgResponse.ok) {
          console.error('Image upload failed');
        }
      }

      // Refresh list and clear form
      await fetchCreators();
      cancelEdit();
    } catch (err: any) {
      console.error('Error saving creator:', err);
      setError(err.message || 'Failed to save creator');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (creatorId: string) => {
    if (!window.confirm('Are you sure you want to delete this creator?')) return;
    
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/admin/creators/${creatorId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete creator');
      await fetchCreators();
      if (selectedCreator?.id === creatorId) {
        cancelEdit();
      }
    } catch (err) {
      console.error('Error deleting creator:', err);
      setError('Failed to delete creator');
    }
  };

  const handleToggleActive = async (creatorId: string) => {
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/admin/creators/${creatorId}/toggle-active`, {
        method: 'PATCH',
      });
      if (!response.ok) throw new Error('Failed to toggle status');
      await fetchCreators();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  };

  // Filter creators by search
  const filteredCreators = creators.filter(c => 
    c.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.area?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Live Preview Card Component (EXACT same style as Creators page)
  const PreviewCard = () => {
    const previewHashtags = formData.hashtags ? formData.hashtags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const hasSocialLinks = formData.website || formData.youtube || formData.tiktok || formData.instagram || formData.linkedin || formData.twitter || formData.twitch;
    const previewRealTalkTags = formData.real_talk_tags ? formData.real_talk_tags.split(',').map(t => t.trim()).filter(Boolean) : [];

    return (
      <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col w-full max-w-sm">
        <div className="px-6 pb-6 pt-3 flex flex-col flex-grow">
          {/* Social Links - Top with divider (same as Creators page) */}
          {hasSocialLinks && (
            <div className="flex items-center justify-center gap-3 pb-2 mb-4 border-b border-gray-200">
              {formData.website && (
                <span className="text-gray-600/70 hover:text-gray-800 transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <GlobeIcon />
                </span>
              )}
              {formData.tiktok && (
                <span className="text-black/70 hover:text-black transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <TikTokIcon />
                </span>
              )}
              {formData.instagram && (
                <span className="text-pink-500/70 hover:text-pink-600 transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <InstagramIcon />
                </span>
              )}
              {formData.youtube && (
                <span className="text-red-500/70 hover:text-red-600 transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <YouTubeIcon />
                </span>
              )}
              {formData.linkedin && (
                <span className="text-blue-600/70 hover:text-blue-700 transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <LinkedInIcon />
                </span>
              )}
              {formData.twitter && (
                <span className="text-gray-800/70 hover:text-black transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <TwitterIcon />
                </span>
              )}
              {formData.twitch && (
                <span className="text-purple-500/70 hover:text-purple-600 transition-colors p-1.5 rounded-full hover:bg-gray-100">
                  <TwitchIcon />
                </span>
              )}
            </div>
          )}

          {/* Profile Image - Squared with rounded corners (same as Creators page) */}
          <div className="flex justify-center mb-2">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt={formData.display_name || 'Preview'}
                className="w-24 h-24 rounded-2xl object-cover shadow-md"
              />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-pink-400 to-orange-300 flex items-center justify-center shadow-md">
                <span className="text-3xl text-white">
                  {formData.display_name ? formData.display_name.charAt(0).toUpperCase() : '?'}
                </span>
              </div>
            )}
          </div>

          {/* Name & Area (same as Creators page) */}
          <div className="text-center">
            <h3 className="text-lg font-bold text-gray-900">
              {formData.display_name || 'Display Name'}
            </h3>
            <p className="text-sm text-pink-600 font-medium">
              {formData.area || 'Area / Topic'}
            </p>
          </div>

          {/* Hashtags - Gen Z style (same as Creators page) */}
          {previewHashtags.length > 0 && (
            <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5 mt-1">
              {previewHashtags.map((tag, idx) => (
                <span key={idx} className="text-sm font-bold text-gray-900 tracking-tight">
                  {tag.startsWith('#') ? tag : `#${tag}`}
                </span>
              ))}
            </div>
          )}

          {/* About Me */}
          {formData.about_me && (
            <div className="mt-3">
              <p className="text-gray-600 text-sm text-left leading-relaxed">
                {formData.about_me.length > 120 
                  ? formData.about_me.slice(0, 120) + '...' 
                  : formData.about_me}
              </p>
            </div>
          )}

          {/* Real Talk Section (same as Creators page) */}
          {(formData.real_talk_sentence || previewRealTalkTags.length > 0) && (
            <div className="mt-3 bg-gradient-to-br from-pink-50 to-yellow-50 rounded-lg p-3 border border-pink-100 flex-grow">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-sm">🗣️</span>
                <span className="text-xs font-semibold text-pink-700">Real Talk</span>
              </div>
              {formData.real_talk_sentence && (
                <p className="text-[13px] text-gray-700 italic mb-2">"{formData.real_talk_sentence}"</p>
              )}
              {previewRealTalkTags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {previewRealTalkTags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-600 to-pink-500 text-white"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Status Badge */}
          <div className="mt-4 flex justify-center">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              formData.is_active 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-500'
            }`}>
              {formData.is_active ? '✅ Active' : '⏸️ Inactive'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-pink-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">🎨 Creator Management</h1>
              <p className="text-sm text-gray-500 mt-1">Manage education content creators</p>
            </div>
            <button
              onClick={startCreating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-md"
            >
              <Plus size={18} />
              New Creator
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        <div className="flex gap-6">
          {/* Left: List */}
          <div className="w-1/3 min-w-[300px]">
            {/* Search */}
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search creators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>

            {/* Creator List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : filteredCreators.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No creators found</div>
              ) : (
                <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                  {filteredCreators.map((creator) => (
                    <div
                      key={creator.id}
                      onClick={() => selectCreator(creator)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedCreator?.id === creator.id ? 'bg-pink-50 border-l-4 border-pink-500' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400 to-orange-300 flex items-center justify-center overflow-hidden shadow-sm">
                          {creator.profile_image_url ? (
                            <img src={getImageUrl(creator.profile_image_url) || ''} alt={creator.display_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-lg text-white font-bold">{creator.display_name.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{creator.display_name}</h3>
                          <p className="text-sm text-gray-500 truncate">{creator.area || 'No area'}</p>
                        </div>
                        <span className={`w-2 h-2 rounded-full ${creator.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <p className="mt-2 text-sm text-gray-500 text-center">
              {filteredCreators.length} creator{filteredCreators.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Right: Edit Form + Preview */}
          <div className="flex-1">
            {(selectedCreator || isCreating) ? (
              <div className="flex gap-6">
                {/* Edit Form */}
                <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {isCreating ? '✨ New Creator' : `✏️ Edit: ${selectedCreator?.display_name}`}
                    </h2>
                    <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600">
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image</label>
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-pink-400 to-orange-300 flex items-center justify-center overflow-hidden shadow-md">
                          {imagePreview ? (
                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-2xl text-white font-bold">{formData.display_name ? formData.display_name.charAt(0).toUpperCase() : '?'}</span>
                          )}
                        </div>
                        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors">
                          <Upload size={16} />
                          Upload
                          <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                        </label>
                      </div>
                    </div>

                    {/* Display Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                      <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        placeholder="Max Mustermann"
                      />
                    </div>

                    {/* Area */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Area / Topic</label>
                      <input
                        type="text"
                        value={formData.area}
                        onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        placeholder="Karriere & Bewerbung"
                      />
                    </div>

                    {/* Hashtags - right after Area */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hashtags</label>
                      <input
                        type="text"
                        value={formData.hashtags}
                        onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        placeholder="#karriere, #bewerbung, #tipps"
                      />
                      <p className="text-xs text-gray-400 mt-1">Comma-separated</p>
                    </div>

                    {/* About Me */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">About Me</label>
                      <textarea
                        value={formData.about_me}
                        onChange={(e) => setFormData({ ...formData, about_me: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                        placeholder="Short bio..."
                      />
                    </div>

                    {/* Real Talk Section */}
                    <div className="border-t border-gray-200 pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">🗣️ Real Talk</label>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Sentence (quote)</label>
                          <input
                            type="text"
                            value={formData.real_talk_sentence}
                            onChange={(e) => setFormData({ ...formData, real_talk_sentence: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="I once quit my job without a plan..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Tags (comma-separated)</label>
                          <input
                            type="text"
                            value={formData.real_talk_tags}
                            onChange={(e) => setFormData({ ...formData, real_talk_tags: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="no_connections, passion, career_change"
                          />
                          <p className="text-xs text-gray-400 mt-1">Keys: imposter, rejected, no_connections, hustle, failed_before, passion, career_change, self_taught, late_bloomer, unconventional, burnout, immigrant, first_gen, dropout, pivot, career_break, doubted_path, lucky_job, quit_no_plan</p>
                        </div>
                      </div>
                    </div>

                    {/* Social Links */}
                    <div className="border-t border-gray-200 pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Social Links</label>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <GlobeIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://yourwebsite.com"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <TikTokIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.tiktok}
                            onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://tiktok.com/@username"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <InstagramIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.instagram}
                            onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://instagram.com/username"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <YouTubeIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.youtube}
                            onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://youtube.com/@channel"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <LinkedInIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.linkedin}
                            onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://linkedin.com/in/username"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <TwitterIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.twitter}
                            onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://twitter.com/username"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <TwitchIcon className="w-5 h-5 text-gray-400" />
                          <input
                            type="url"
                            value={formData.twitch}
                            onChange={(e) => setFormData({ ...formData, twitch: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                            placeholder="https://twitch.tv/username"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Language - Multi-select toggle */}
                    <div className="border-t border-gray-200 pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Language(s)</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const langs = formData.language.split(',').filter(Boolean);
                            const hasDE = langs.includes('de');
                            let newLangs: string[];
                            if (hasDE) {
                              newLangs = langs.filter(l => l !== 'de');
                            } else {
                              newLangs = [...langs, 'de'];
                            }
                            // Ensure at least one language
                            if (newLangs.length === 0) newLangs = ['de'];
                            setFormData({ ...formData, language: newLangs.join(',') });
                          }}
                          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all border ${
                            formData.language.includes('de')
                              ? 'bg-pink-100 text-pink-700 border-pink-300'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-pink-200 hover:bg-pink-50'
                          }`}
                        >
                          🇩🇪 Deutsch
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const langs = formData.language.split(',').filter(Boolean);
                            const hasEN = langs.includes('en');
                            let newLangs: string[];
                            if (hasEN) {
                              newLangs = langs.filter(l => l !== 'en');
                            } else {
                              newLangs = [...langs, 'en'];
                            }
                            // Ensure at least one language
                            if (newLangs.length === 0) newLangs = ['en'];
                            setFormData({ ...formData, language: newLangs.join(',') });
                          }}
                          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all border ${
                            formData.language.includes('en')
                              ? 'bg-pink-100 text-pink-700 border-pink-300'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-pink-200 hover:bg-pink-50'
                          }`}
                        >
                          🇬🇧 English
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Select one or both languages</p>
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center gap-3 pt-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                      </label>
                      <span className="text-sm font-medium text-gray-700">Active (visible on Creators page)</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                    {selectedCreator && (
                      <button
                        onClick={() => handleDelete(selectedCreator.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    )}
                    <div className="flex gap-3 ml-auto">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving || !formData.display_name}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Check size={16} />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Live Preview */}
                <div className="w-96">
                  <div className="sticky top-24 flex flex-col items-center">
                    <h3 className="text-sm font-medium text-gray-500 mb-3">📱 Live Preview</h3>
                    <PreviewCard />
                    <p className="text-xs text-gray-400 mt-3">
                      This is how the card will appear on the Creators page
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-6xl mb-4">👈</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Select a Creator</h3>
                <p className="text-gray-500">
                  Click on a creator from the list to edit, or create a new one.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorManagement;

